import {
  React, useContext, useState, useEffect, useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import FirebaseContext from '../../../../context/FirebaseContext';
import InstagramMediaContext, { initialValues } from './InstagramMediaContext';

export const InstagramMediaContextProvider = ({ children }) => {
  const [mediaId, setMediaId] = useState();
  const [mediaUrl, setMediaUrl] = useState(initialValues.mediaUrl);
  const [isLoading, setIsLoading] = useState(initialValues.isLoading);
  const [error, setError] = useState(initialValues.error);
  const mediaCache = useRef(new Map());
  const {
    firebase: { storage },
  } = useContext(FirebaseContext);

  useEffect(() => {
    let isCancelled = false;

    const fetchMediaUrl = async () => {
      if (!mediaId) return;

      if (mediaCache.current.has(mediaId)) {
        setMediaUrl(mediaCache.current.get(mediaId));
        setError(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const mediaRef = storage.ref().child(`instagramMedia/${mediaId}`);
        const url = await mediaRef.getDownloadURL();

        mediaCache.current.set(mediaId, url);
        if (isCancelled) return;
        setMediaUrl(url);
        setError(null);
      } catch (e) {
        if (isCancelled) return;
        setMediaUrl(null);
        setError(e);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    fetchMediaUrl();

    return () => {
      isCancelled = true;
    };
  }, [mediaId, storage]);

  const contextValue = useMemo(() => ({
    mediaUrl,
    isLoading,
    error,
    setMediaId,
  }), [mediaUrl, isLoading, error]);

  return (
    <InstagramMediaContext.Provider value={contextValue}>
      {children}
    </InstagramMediaContext.Provider>
  );
};

InstagramMediaContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default observer(InstagramMediaContextProvider);
